import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useAuth } from '@/_shared/hooks/useAuth';
import { clearAccountDrafts } from '@/_shared/lib/account-drafts';
import { loginPathFor } from '@/_shared/lib/login-path';
import { orpc } from '@/_shared/lib/orpc';

import { startFromSharedSession } from '../lib/lookout-session';
import { renewLookoutSession, type RenewOutcome } from '../lib/shared-session';

const CHECK_INTERVAL_MS = 5 * 60_000;
/** config-backend refreshes the ID token only in its last 5 minutes, so the renewal waits for that window. */
const RENEW_BEFORE_EXPIRY_MS = 4 * 60_000;

/** config-backend's address while Lookout's own sign-in uses the shared session (ACT2-879), or null. */
export function useSharedSessionApi(): { apiBaseUrl: string | null; loading: boolean } {
  const { data, isLoading } = useQuery(orpc.auth.providers.queryOptions());
  const nativeSignIn = data?.nativeSignIn;
  return { apiBaseUrl: nativeSignIn?.sharedSession ? nativeSignIn.apiBaseUrl : null, loading: isLoading };
}

/**
 * The silent start on the login page. When config-backend has a session for this browser, Lookout's session starts
 * from it with no prompt, and the page opens `returnTo`. `checking` is true while the start runs and the page leaves.
 */
export function useSharedSessionStart(options: { returnTo: string | null; enabled: boolean; fromWebApp?: boolean }): {
  checking: boolean;
} {
  const { apiBaseUrl } = useSharedSessionApi();
  const start = useQuery({
    queryKey: ['auth', 'sharedSessionStart', options.returnTo],
    queryFn: () =>
      startFromSharedSession(apiBaseUrl ?? '', {
        returnTo: options.returnTo,
        method: 'shared_session',
        fromWebApp: options.fromWebApp === true,
      }),
    enabled: options.enabled && apiBaseUrl !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });
  const redirectTo = start.data?.kind === 'signed_in' ? start.data.redirectTo : null;
  useEffect(() => {
    if (redirectTo) window.location.assign(redirectTo);
  }, [redirectTo]);
  return { checking: options.enabled && (start.isLoading || redirectTo !== null) };
}

/** Checks that start together share one request: a focus and a timer, or a mount that runs twice. */
let pendingCheck: Promise<RenewOutcome> | null = null;

function checkOnce(apiBaseUrl: string, userId: string): Promise<RenewOutcome> {
  pendingCheck ??= renewLookoutSession(apiBaseUrl, { userId }).finally(() => {
    pendingCheck = null;
  });
  return pendingCheck;
}

/**
 * Keeps a Lookout session from the shared session going, and ends it when the shared session ended. It checks when a
 * page opens, every 5 minutes, when the tab gets focus, and shortly before the session expires. When Lookout's session
 * belongs to another user than the page shows, the page reloads as that user.
 */
export function useSharedSessionKeeper(): void {
  const queryClient = useQueryClient();
  const { apiBaseUrl } = useSharedSessionApi();
  const { data: me } = useQuery(orpc.auth.me.queryOptions({ input: {} }));
  const { signOutAndRedirect } = useAuth();
  const expiresAt = me?.user ? (me.sharedSession?.expiresAt ?? null) : null;
  const userId = me?.user?.id ?? null;
  const shownUserId = useRef(userId);

  // Another tab can change the session to another user, and an `auth.me` refetch can show that user before a check
  // runs. A full load, as for `switched`, so that no cached data of the previous user stays on the page.
  useEffect(() => {
    const previous = shownUserId.current;
    shownUserId.current = userId;
    if (apiBaseUrl && previous && userId && previous !== userId) {
      clearAccountDrafts();
      window.location.assign('/');
    }
  }, [apiBaseUrl, userId]);

  useEffect(() => {
    if (!apiBaseUrl || !expiresAt || !userId) return undefined;
    let stopped = false;
    const check = async (): Promise<void> => {
      if (stopped) return;
      const outcome = await checkOnce(apiBaseUrl, userId);
      if (stopped) return;
      if (outcome === 'renewed') {
        await queryClient.invalidateQueries({ queryKey: orpc.auth.me.queryOptions({ input: {} }).queryKey });
      } else if (outcome === 'switched') {
        stopped = true;
        // A full load, so that nothing of the previous user's data stays on the page.
        clearAccountDrafts();
        window.location.assign('/');
      } else if (outcome === 'ended') {
        stopped = true;
        // The site that ended the shared session also ended the provider session.
        const signedOut = await signOutAndRedirect(loginPathFor(window.location, true), {
          keepSharedSession: true,
          keepProviderSession: true,
        });
        // A failed sign-out keeps the page signed in, so the next check tries again.
        if (!signedOut) stopped = false;
      }
    };
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    void check();
    const interval = setInterval(() => void check(), CHECK_INTERVAL_MS);
    // Inside the renewal window already, the check above renews.
    const renewIn = Date.parse(expiresAt) - RENEW_BEFORE_EXPIRY_MS - Date.now();
    const beforeExpiry = renewIn > 0 ? setTimeout(() => void check(), renewIn) : undefined;
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      stopped = true;
      clearInterval(interval);
      clearTimeout(beforeExpiry);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [apiBaseUrl, expiresAt, userId, queryClient, signOutAndRedirect]);
}
